import React, { useCallback, useMemo, useState } from "react";

import {
  AHSeparator,
  AHSwitch,
  Box,
  Circle,
  Input,
  ListItem,
  SectionHeader,
  TouchableBox,
  useTheme,
} from "arkh";

import { FormattedMessage } from "react-intl";
import { useShowCancelWarningAlert } from "../../../common/useCancelWarning";

import { graphql, useLazyLoadQuery, useMutation } from "react-relay";

import { LazyQueryNetworkBoundrary } from "../../../components/mobile/QueryNetworkBoundrary";
import {
  StoreAdminNavigationProp,
  StoreAdminRouteProps,
} from "../../StoreAdmin/StoreAdminNavigation";

import { EntityHeader } from "@/src/components/EntityHeader";
import { useNavigationHeader } from "@/src/common/navigationHeaderOptions";
import { usePreventRemove } from "@react-navigation/native";

import { onShareQR } from "../onShareQR";

import { ConnectAdminSiteAdminQuery } from "./__generated__/ConnectAdminSiteAdminQuery.graphql";
import {
  ConnectAdminSiteAdminSaveMutation,
  ConnectConfigUpdateInput,
} from "./__generated__/ConnectAdminSiteAdminSaveMutation.graphql";
import { KeyboardAwareScrollView } from "react-native-keyboard-controller";
import { ShareAndDownload } from "../ShareAndDownload";
import { PhoneInput } from "../PhoneInput";
import { Platform } from "react-native";
import * as Clipboard from "expo-clipboard";

type NavigationProp = StoreAdminNavigationProp<"ConnectAdminSiteAdmin">;
type Props = {
  navigation: NavigationProp;
  route: StoreAdminRouteProps<"ConnectAdminSiteAdmin">;
};

export function ConnectAdminSiteAdminScreen(props: Props) {
  return (
    <LazyQueryNetworkBoundrary>
      {(fk) => <ConnectAdminSiteAdmin {...props} fetchKey={fk} />}
    </LazyQueryNetworkBoundrary>
  );
}

function ConnectAdminSiteAdmin(props: Props & { fetchKey: string }) {
  const data = useLazyLoadQuery<ConnectAdminSiteAdminQuery>(
    graphql`
      query ConnectAdminSiteAdminQuery {
        store {
          id
          name
          connectConfig {
            color
            email
            phoneNumber
            facebookLink
            instagramLink
            site {
              welcomeMessage
              publicUrl
              active
              qrCode {
                svg
              }
            }
          }
        }
      }
    `,
    {},
    {
      fetchKey: props.fetchKey,
      fetchPolicy: "store-and-network",
    }
  );

  const [_description, setDescription] = useState<string | null>(null);
  const [_color, setColor] = useState<string | null>(null);

  const [_phoneNumber, setPhoneNumber] = useState<string | null>(null);
  const [_email, setEmail] = useState<string | null>(null);
  const [_instagramLink, setInstagramLink] = useState<string | null>(null);
  const [_facebookLink, setFacebookLink] = useState<string | null>(null);
  const [_siteActive, setSiteActive] = useState<boolean | null>(null);

  function resetState() {
    setColor(null);
    setSiteActive(null);
    setDescription(null);
    setPhoneNumber(null);
    setEmail(null);
    setInstagramLink(null);
    setFacebookLink(null);
  }

  const showCancelWarningAlert = useShowCancelWarningAlert();

  const input = useMemo(
    (): ConnectConfigUpdateInput => ({
      setColor: _color,
      setEmail: _email,
      setPhoneNumber: _phoneNumber,
      setInstagramLink: _instagramLink,
      setFacebookLink: _facebookLink,
      setCustomerIndexSiteActive: _siteActive,
      setCustomerIndexSiteWelcomeMessage: _description,
    }),
    [
      _color,
      _description,
      _email,
      _facebookLink,
      _instagramLink,
      _phoneNumber,
      _siteActive,
    ]
  );

  const isDirty = useMemo(
    () => Object.values(input).some((value) => value !== null),
    [input]
  );

  const [mutate, isPending] = useMutation<ConnectAdminSiteAdminSaveMutation>(
    graphql`
      mutation ConnectAdminSiteAdminSaveMutation(
        $input: ConnectConfigUpdateInput!
      ) {
        store {
          updateConnectConfig(input: $input) {
            store {
              connectConfig {
                color
                email
                phoneNumber
                facebookLink
                instagramLink
                site {
                  active
                  publicUrl
                  welcomeMessage
                  qrCode {
                    svg
                  }
                }
              }
            }
          }
        }
      }
    `
  );
  const onSave = useCallback(() => {
    mutate({
      variables: {
        input,
      },
      onCompleted: () => {
        resetState();
      },
    });
  }, [input, mutate]);

  useNavigationHeader({
    type: "SAVE",
    onCancel: props.navigation.goBack,
    onSave: onSave,
    saveLoading: isPending,
    disabled: !isDirty,
    title: "",
    defaultContentStyle: true,
  });
  usePreventRemove(isDirty, (e) => {
    showCancelWarningAlert(() => {
      props.navigation.dispatch(e.data.action);
    });
  });

  const isSiteActive =
    _siteActive ?? data.store.connectConfig?.site?.active ?? false;

  return (
    <KeyboardAwareScrollView
      contentInsetAdjustmentBehavior="automatic"
      bottomOffset={50}
    >
      <SiteColorSection
        color={_color ?? data.store.connectConfig?.color ?? null}
        setColor={setColor}
      />
      <Box space="medium">
        <ListItem
          border="border-primary"
          gap="standard"
          isFirst
          isLast={!isSiteActive}
          right={
            <AHSwitch value={isSiteActive} onValueChange={setSiteActive} />
          }
        >
          <ListItem.Title>
            <FormattedMessage
              defaultMessage="Enable Landing Page"
              description="Connect Admin Site Admin: Welcome message Title"
            />
          </ListItem.Title>
        </ListItem>
        {isSiteActive ? (
          <>
            <AHSeparator />
            <ListItem border="border-primary" gap="standard">
              <ListItem.Title>
                <FormattedMessage
                  defaultMessage="Welcome message"
                  description="Connect Admin Site Admin: Welcome message Title"
                />
              </ListItem.Title>
              <Input
                value={
                  _description ??
                  data.store.connectConfig?.site?.welcomeMessage ??
                  ""
                }
                onChangeText={setDescription}
              />
            </ListItem>
            <AHSeparator />
            <ListItem border="border-primary" gap="standard" isLast>
              {data.store.connectConfig?.site?.publicUrl ? (
                <Box style={{ flexDirection: "row" }} gap="medium">
                  <EntityHeader.Button
                    onPress={() => {
                      if (data.store.connectConfig?.site?.qrCode?.svg) {
                        onShareQR(data.store.connectConfig?.site?.qrCode?.svg);
                      }
                    }}
                  >
                    <ShareAndDownload />
                  </EntityHeader.Button>
                  <EntityHeader.Button
                    onPress={() => {
                      if (data.store.connectConfig?.site?.publicUrl) {
                        props.navigation.navigate("ConnectAdminPreview", {
                          url:
                            data.store.connectConfig?.site?.publicUrl +
                            "?key=" +
                            Date.now(),
                        });
                      }
                    }}
                  >
                    <FormattedMessage
                      defaultMessage="View Page"
                      description="Connect Admin Site Admin: View Page Button Title"
                    />
                  </EntityHeader.Button>
                  <EntityHeader.Button
                    onPress={async () => {
                      const publicUrl =
                        data.store.connectConfig?.site?.publicUrl;
                      if (publicUrl) {
                        if (Platform.OS === "web") {
                          navigator.clipboard.writeText(publicUrl);
                        } else {
                          await Clipboard.setUrlAsync(publicUrl);
                        }
                      }
                    }}
                  >
                    <FormattedMessage
                      defaultMessage="Copy link"
                      description="Connect Admin Input: Copy link Button Title"
                    />
                  </EntityHeader.Button>
                </Box>
              ) : (
                <>
                  <ListItem.DetailActionText>
                    <FormattedMessage
                      defaultMessage="Save to active Landing Page"
                      description="Connect Admin Site Admin: Landing Page URL Title"
                    />
                  </ListItem.DetailActionText>
                </>
              )}
            </ListItem>
          </>
        ) : null}
      </Box>

      <SectionHeader>
        <SectionHeader.Title>
          <FormattedMessage
            defaultMessage="Contact Information"
            description="Connect Admin Site Admin: Contact Information Title"
          />
        </SectionHeader.Title>
        <SectionHeader.Description>
          <FormattedMessage
            defaultMessage="Add your contact information to allow customers to get in touch with you. "
            description="Connect Admin Site Admin: Contact Information Description"
          />
        </SectionHeader.Description>
      </SectionHeader>
      <Box space="medium" spaceTop="none">
        <ListItem border="border-primary" gap="standard" isFirst>
          <ListItem.Title>
            <FormattedMessage
              defaultMessage="Email"
              description="Connect Admin Site Admin: Email Title"
            />
          </ListItem.Title>
          <Input
            value={_email ?? data.store.connectConfig?.email ?? ""}
            onChangeText={setEmail}
            autoComplete="email"
            keyboardType="email-address"
            textContentType="emailAddress"
            placeholder="your@email.com"
          />
        </ListItem>
        <AHSeparator />
        <ListItem border="border-primary" gap="standard">
          <ListItem.Title>
            <FormattedMessage
              defaultMessage="Phone Number"
              description="Connect Admin Site Admin: Phone Number Title"
            />
          </ListItem.Title>

          <PhoneInput
            value={_phoneNumber ?? data.store.connectConfig?.phoneNumber ?? ""}
            onChangeText={setPhoneNumber}
            placeholder="+46 123 456 789"
            showValidation={true}
          />
        </ListItem>
        <AHSeparator />
        <ListItem border="border-primary" gap="standard">
          <ListItem.Title>
            <FormattedMessage
              defaultMessage="Instagram Link"
              description="Connect Admin Site Admin: Instagram Link Title"
            />
          </ListItem.Title>
          <Input
            value={
              _instagramLink ?? data.store.connectConfig?.instagramLink ?? ""
            }
            onChangeText={setInstagramLink}
            placeholder="https://www.instagram.com/your_profile"
          />
        </ListItem>
        <AHSeparator />
        <ListItem border="border-primary" gap="standard" isLast>
          <ListItem.Title>
            <FormattedMessage
              defaultMessage="Facebook Link"
              description="Connect Admin Site Admin: Facebook Link Title"
            />
          </ListItem.Title>
          <Input
            value={
              _facebookLink ?? data.store.connectConfig?.facebookLink ?? ""
            }
            onChangeText={setFacebookLink}
            placeholder="https://www.facebook.com/your_page"
          />
        </ListItem>
      </Box>
    </KeyboardAwareScrollView>
  );
}

function SiteColorSection(props: {
  color: string | null;
  setColor: (newColor: string) => void;
}) {
  const theme = useTheme();

  const colors = useMemo(
    () => [
      theme.primary,
      "#AEF34C",
      "#00A44A",
      "rgb(220, 56,57)",
      "#4A2772",
      "#00527E",
      "#CF4323",
      "#FFD100",
    ],
    [theme.primary]
  );

  return (
    <>
      <Box space="medium">
        <ListItem border="border-primary" gap="standard" isFree>
          <ListItem.Title>
            <FormattedMessage
              defaultMessage="Theme Color"
              description="Connect Admin: Site Color Title"
            />
          </ListItem.Title>
          <ListItem.SubTitle>
            <FormattedMessage
              defaultMessage="Choose a color that best represents your brand. This will be utilized for buttons and text throughout the pages."
              description="Connect Admin: Site Color Subtitle"
            />
          </ListItem.SubTitle>
          <Box
            style={{
              flexDirection: "row",
              flexWrap: "wrap",
            }}
          >
            {colors.map((c) => (
              <ColorOption
                key={c}
                color={c}
                onPress={() => props.setColor(c)}
                selected={c === props.color}
              />
            ))}
          </Box>
        </ListItem>
      </Box>
    </>
  );
}

function ColorOption(props: {
  onPress: () => void;
  color: string;
  selected: boolean;
}) {
  return (
    <TouchableBox space="medium" onPress={props.onPress}>
      <Box
        style={{
          width: 45,
          height: 45,
          borderRadius: 30,
          borderWidth: 4,
          alignItems: "center",
          justifyContent: "center",
        }}
        borderColor={props.selected ? "darkText" : "transparent"}
      >
        <Circle
          size="medium"
          style={{
            backgroundColor: props.color,
          }}
        />
      </Box>
    </TouchableBox>
  );
}
